<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { computed } from 'vue'
import type { HomePageConfig } from '../../configs/types'
import ModuleCard from './ModuleCard.vue'
import BizBookCover from '@/components/BizBookCover.vue'
import BizShowTags from '@/components/BizShowTags.vue'

const props = defineProps<{
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  config: HomePageConfig
  cardClass?: string
  headerClass?: string
  gotoBook: (id: string) => void
}>()

const feedRewardsBg = '/images/talewave/bg_feed_rewards.png'

// 生成唯一 id 用于 BizBookCover 的 intersection observer
function genId(prefix = 'bw') {
  return `${prefix}-${uuid()}`
}

// 使用 JS 将 items 交替分配到左右两列，保证两列布局在各种环境下一致
// 左列已固定放置占位图，因此书籍从 feeds[0] 开始按“右列 → 左列 → 右列 → 左列”交替分配
function splitColumns(items: any[]) {
  const left: any[] = []
  const right: any[] = []
  items.forEach((it, idx) => {
    if ((idx % 2) === 0)
      right.push(it) // 第 1、3、5…本书 → 右列
    else
      left.push(it) // 第 2、4、6…本书 → 左列（接在占位图下方）
  })
  return { left, right }
}

const columns = computed(() => splitColumns(props.feeds || []))
</script>

<template>
  <ModuleCard
    v-if="feeds.length"
    :title-text="feedShowName"
    :show-action="false"
    :card-class="props.cardClass"
    :header-class="props.headerClass"
  >
    <div class="talewave-masonry mt-10px">
      <div class="col">
        <!-- 第一张固定占位图 -->
        <div class="masonry-item placeholder">
          <img :src="feedRewardsBg" alt="feed rewards" class="w-full rounded-12px">
        </div>

        <div v-for="item in columns.left" :key="`l-${item.id}`" class="masonry-item" @click="gotoBook(item.id)">
          <BizBookCover
            :id="genId('l')"
            :src="item.contentCoverUrl || ''"
            :extra="{
              module_name: feedShowName,
              module_id: feedId,
              module_type: feedsColumnType,
              author: item.authorInfo?.author,
              ...item,
            }"
            class="feed-cover w-full"
          />
          <div class="card-body">
            <p class="title">
              {{ item.contentName?.replace(/&nbsp;/g, ' ') ?? '' }}
            </p>
            <p class="desc">
              {{ item.introduce }}
            </p>
            <BizShowTags v-if="item.tagList && item.tagList.length" :tags="item.tagList" class="mt-10px" />
          </div>
        </div>
      </div>

      <div class="col">
        <div v-for="item in columns.right" :key="`r-${item.id}`" class="masonry-item" @click="gotoBook(item.id)">
          <BizBookCover
            :id="genId('r')"
            :src="item.contentCoverUrl || ''"
            :extra="{
              module_name: feedShowName,
              module_id: feedId,
              module_type: feedsColumnType,
              author: item.authorInfo?.author,
              ...item,
            }"
            class="feed-cover w-full"
          />
          <div class="card-body">
            <p class="title">
              {{ item.contentName?.replace(/&nbsp;/g, ' ') ?? '' }}
            </p>
            <p class="desc">
              {{ item.introduce }}
            </p>
            <BizShowTags v-if="item.tagList && item.tagList.length" :tags="item.tagList" class="mt-10px" />
          </div>
        </div>
      </div>
    </div>
  </ModuleCard>
</template>

<style scoped>
.talewave-masonry {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: start;
}
.talewave-masonry .col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.masonry-item {
  width: 100%;
  margin-bottom: 0;
}
.masonry-item img {
  display: block;
  width: 100%;
  height: auto;
}
.masonry-item .card-body {
  /* 每本书的圆角背景 */
  background: #F8FAFC;
  padding: 12px;
  border-radius: 12px;
  /* 去掉上方间隙并向上覆盖 1px 以修复细小缝隙（跨设备的抗锯齿/四舍五入问题） */
  margin-top: -1px;
}

/* 封面与卡片背景无缝连接：
   - 封面去掉下半部分圆角（只保留上角）
   - 卡片背景去掉上半部分圆角（只保留下角）
*/
.feed-cover {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  display: block;
}

/* 确保内层保持相同裁切（BizBookCover 内有一个内层 div 作为高度容器） */
.feed-cover > div {
  overflow: hidden;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.feed-cover img {
  /* 图片自身去掉下角圆角，顶部保留圆角（由容器裁切也能生效） */
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  display: block;
}

.masonry-item .card-body {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.masonry-item .title {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  color: var(--text-primary, #32353A);
  margin-bottom: 6px;
  /* 最多两行，超出省略 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.masonry-item .desc {
  font-size: 13px;
  color: var(--text-tertiary, #9296A0);
  line-height: 1.4;
  /* 最多两行，超出省略 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.placeholder img {
  width: 100%;
  object-fit: cover;
  height: 100px; /* 固定占位高度，可按需调整 */
}
</style>
